import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipLoader } from 'react-spinners';
import { clearSession } from '../auth';
import './QuantityProducts.css';

const QuantityProducts = () => {
  const { menuName } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [idAccountCaisse, setIdAccountCaisse] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateRange, setDateRange] = useState([]);

  // Generate 8 days (current day + 7 future days)
  const generateDateRange = useCallback(() => {
    const today = new Date();
    const dates = [];
    for (let i = 0; i < 8; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    setDateRange(dates);
  }, []);

  // Format date for display (e.g., "17")
  const formatDay = (date) => {
    return date.getDate();
  };

  // Format date for API (DD-MM-YYYY)
  const formatDateForAPI = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format date for card display (YYYY/MM/DD)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) {
      // Fallback to selected date in DD-MM-YYYY, then convert to YYYY/MM/DD
      const fallbackDate = formatDateForAPI(selectedDate);
      const [day, month, year] = fallbackDate.split('-');
      return `${year}/${month}/${day}`;
    }
    // Expect dateString in DD-MM-YYYY format
    const [day, month, year] = dateString.split('-');
    return `${year}/${month}/${day}`; // Convert to YYYY/MM/DD
  };

  // Fetch products for the selected date
  const fetchProducts = useCallback(
    async (date) => {
      try {
        setLoading(true);
        setError('');
        const id_caisse = localStorage.getItem('id_caisse');
        const id_account_caisse = localStorage.getItem('id_account_caisse');

        if (!id_caisse) {
          throw new Error('Aucun identifiant de restaurant trouvé dans localStorage');
        }
        if (!id_account_caisse) {
          throw new Error('Aucun identifiant de compte caisse trouvé dans localStorage');
        }

        setIdAccountCaisse(id_account_caisse);

        const response = await axios.post('https://apikitchendisplay.lalivraison.ma/api/livelivraison', {
          restaurant_id: id_caisse,
          date: formatDateForAPI(date),
          nom_point: decodeURIComponent(menuName),
          id_account_caisse,
        });

        const orders = response.data.neworders || [];
        console.log('Fetched orders:', JSON.stringify(orders, null, 2)); // Debug log

        // Map flat neworders array to products
        const allProducts = orders.map((order) => ({
          id_prix: order.id_prix || 'inconnu',
          designation: order.designation,
          label: order.label,
          total_quantity: order.total_quantity !== undefined ? order.total_quantity : 0,
          id_caisse: order.id_caisse || id_caisse,
          date_remboursement: order.date_remboursement || formatDateForAPI(date), // Expect DD-MM-YYYY
        }));

        setProducts(allProducts);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.error || 'Échec de la récupération des produits');
        setLoading(false);
      }
    },
    [menuName]
  );

  // Handle date selection
  const handleDateClick = (date) => {
    setSelectedDate(date);
    fetchProducts(date);
  };

  // Initialize date range and fetch products for today
  useEffect(() => {
    generateDateRange();
    fetchProducts(selectedDate);
  }, [generateDateRange, fetchProducts, selectedDate]);

  // Check for day change (e.g., midnight transition)
  useEffect(() => {
    const checkDayChange = () => {
      const now = new Date();
      if (now.getDate() !== selectedDate.getDate()) {
        generateDateRange();
        setSelectedDate(now);
        fetchProducts(now);
      }
    };

    const interval = setInterval(checkDayChange, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [selectedDate, fetchProducts, generateDateRange]);

  const handleLogout = () => {
    clearSession();
    navigate('/');
  };

  const handleMenuSelection = () => {
    navigate('/quantity-menu-selection');
  };

  const handleCardClick = (product) => {
    setSelectedProduct(product);
    setIsPopupOpen(true);
    setUpdateStatus(null);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedProduct(null);
    setUpdateStatus(null);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  const handleNumberClick = async (number) => {
    if (!selectedProduct) {
      setUpdateStatus('erreur : Aucun produit sélectionné. Veuillez réessayer.');
      return;
    }

    const missingFields = {
      id_prix: !selectedProduct.id_prix,
      id_caisse: !selectedProduct.id_caisse,
      total_quantity: selectedProduct.total_quantity === undefined,
    };

    if (Object.values(missingFields).some((missing) => missing)) {
      setUpdateStatus('erreur : Données produit requises manquantes : id_prix, id_caisse, ou total_quantity. Veuillez réessayer.');
      return;
    }

    const requestData = {
      idPrix: selectedProduct.id_prix,
      idCaisse: selectedProduct.id_caisse,
      quantity: selectedProduct.total_quantity,
      number: number,
      date_remboursement: selectedProduct.date_remboursement, // Send DD-MM-YYYY
      id_account_caisse: idAccountCaisse,
    };

    try {
      setUpdateStatus('chargement');
      await axios.post('https://apikitchendisplay.lalivraison.ma/api/update-quantity-products', requestData);
      // Add delay to ensure DB transaction is committed
      await new Promise((resolve) => setTimeout(resolve, 500));
      // Clear products state to force refresh
      setProducts([]);
      // Update selectedProduct quantity for immediate popup feedback
      setSelectedProduct((prev) => ({
        ...prev,
        total_quantity: prev.total_quantity - number,
      }));
      await fetchProducts(selectedDate);
      setUpdateStatus('succès');
      setTimeout(() => {
        closePopup();
      }, 1000);
    } catch (error) {
      setUpdateStatus(`erreur : Échec de la mise à jour de la quantité : ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <div className="app-container">
      <header className="header">
        <div className="logo">
          <img src="/lacaisse_logo.png" alt="Logo La Caisse" />
        </div>
        <div className="header-title">
          <h1>{decodeURIComponent(menuName)}</h1>
        </div>
        <div className="header-buttons">
          <button className="menu-selection-btn" onClick={handleMenuSelection}>
            Points de Préparation
          </button>
          <button className="fullscreen-btn" onClick={toggleFullscreen}>
            {isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            Déconnexion
          </button>
        </div>
      </header>

      <div className="date-nav-container">
        <div className="date-nav">
          {dateRange.map((date, index) => (
            <button
              key={index}
              className={`date-btn ${selectedDate.getDate() === date.getDate() ? 'active' : ''}`}
              onClick={() => handleDateClick(date)}
            >
              {formatDay(date)}
            </button>
          ))}
        </div>
      </div>

      <div className="quantity-products-container">
        {loading ? (
          <div className="spinner-container">
            <ClipLoader color="#3498db" size={50} />
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
            <button
              onClick={() => fetchProducts(selectedDate)}
              style={{
                padding: '8px 16px',
                background: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                marginTop: '10px',
                cursor: 'pointer',
              }}
            >
              Réessayer
            </button>
          </div>
        ) : (
          <div className="products-grid-wrapper">
            <div className="products-grid">
              {products.length > 0 ? (
                products.map((product, index) => (
                  <div
                    key={`${product.id_prix}-${index}`}
                    className="product-card"
                    onClick={() => handleCardClick(product)}
                  >
                    <p className="product-date">Date : {formatDateForDisplay(product.date_remboursement)}</p>
                    <div className="product-name-container">
                      <h3>{product.designation}</h3>
                    </div>
                    <div className="product-details-row">
                      {product.label && (
                        <div className="product-label-container">
                          <span className="product-label">{product.label}</span>
                        </div>
                      )}
                      <div className="product-quantity-container">
                        <span className="product-quantity-label">Quantité</span>
                        <span className="product-quantity">{product.total_quantity}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p>Aucun produit trouvé pour ce point de préparation.</p>
              )}
            </div>
          </div>
        )}

        {isPopupOpen && (
          <div className="popup-overlay" onClick={closePopup}>
            <div className="popup-content" onClick={(e) => e.stopPropagation()}>
              <button className="popup-close-btn" onClick={closePopup}>
                ×
              </button>
              <h2>{selectedProduct?.designation}</h2>
              <p className="popup-quantity">Quantité actuelle : {selectedProduct?.total_quantity}</p>
              {updateStatus === 'chargement' && (
                <div className="popup-status popup-loading">
                  <ClipLoader color="#3498db" size={20} />
                  <span>Mise à jour...</span>
                </div>
              )}
              {updateStatus === 'succès' && (
                <div className="popup-status popup-success">
                  <span>✔ Quantité mise à jour avec succès !</span>
                </div>
              )}
              {updateStatus && updateStatus.startsWith('erreur') && (
                <div className="popup-status popup-error">
                  <span>{updateStatus.replace('erreur : ', '')}</span>
                </div>
              )}
              <div className="number-list">
                {Array.from({ length: selectedProduct?.total_quantity || 1 }, (_, i) => i + 1).map((number) => (
                  <button
                    key={number}
                    className="number-btn"
                    onClick={() => handleNumberClick(number)}
                    disabled={updateStatus === 'chargement'}
                  >
                    {number}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuantityProducts;